import { useEffect, useRef, useState } from 'react';

/**
 * Cloudflare Turnstile for React surfaces.
 *
 * Mirrors the logic already proven on the standalone /beta page
 * (frontend/templates/beta.html) so both surfaces behave identically. Extracted as a hook
 * because the modal is the second consumer — if a third unauthenticated form ever appears,
 * it uses this rather than growing a third copy that drifts.
 *
 * TWO INVIOLABLE RULES ARE ENCODED HERE:
 *
 * 1. PRODUCTION ONLY (Vincent 2026-07-30). On Dev, Turnstile must not exist in ANY observable
 *    way: no widget, no Cloudflare <script>, no network call, no behavioural difference. That is
 *    decided by `window.__TURNSTILE_REQUIRED__`, injected server-side from HEALIST_ENV — never
 *    from hostname or anything a client controls. When it is false this hook does nothing at all.
 *
 * 2. DEGRADE, NEVER DEAD-END (memory/feedback_client_never_stricter_than_server). Every way the
 *    check can fail to yield a token — script blocked by an extension, script stalls, challenge
 *    errors, challenge times out, token expires — resolves to the SAME state: `unavailable=true`
 *    and the caller is expected to let the user proceed. The client must never invent a stricter
 *    contract than the server enforces. The server decides what a token-less request is worth;
 *    for beta-check that means a tighter probe budget, NOT a locked-out human.
 *
 * Returns:
 *   required     — is Turnstile active at all (false on Dev; caller can ignore everything else)
 *   token        — the verified token string, or '' if none yet
 *   unavailable  — the check could not yield a token; caller should proceed without one
 *   ready        — token OR unavailable; i.e. "stop waiting, let them submit"
 *   mountRef     — attach to the <div> the widget should render into
 *   reset()      — discard the current token and re-challenge (call after a failed submit,
 *                  since Turnstile tokens are single-use)
 */
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const SCRIPT_ID = 'cf-turnstile-script';
const LOAD_TIMEOUT_MS = 8000;

export function useTurnstile() {
  const required = window.__TURNSTILE_REQUIRED__ === true;
  const sitekey = window.__TURNSTILE_SITEKEY__ || '';

  const [token, setToken] = useState('');
  const [unavailable, setUnavailable] = useState(false);
  const mountRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    // Dev: do nothing whatsoever. No script tag, no widget, no request.
    if (!required || !sitekey) return undefined;

    let cancelled = false;
    let timeoutId = null;

    const degrade = () => {
      if (!cancelled) setUnavailable(true);
    };

    const render = () => {
      if (cancelled || !mountRef.current) return;
      if (!window.turnstile || typeof window.turnstile.render !== 'function') return degrade();
      try {
        widgetIdRef.current = window.turnstile.render(mountRef.current, {
          sitekey,
          callback: (t) => {
            if (cancelled) return;
            setToken(t || '');
            setUnavailable(false);
          },
          // Any failure mode → the single degrade path. An expired token is a failure to HOLD a
          // token, so it degrades too rather than silently leaving a stale value in place.
          'error-callback': degrade,
          'timeout-callback': degrade,
          'expired-callback': () => {
            if (cancelled) return;
            setToken('');
            degrade();
          },
        });
      } catch (_) {
        degrade();
      }
    };

    // Script may already be present if another surface mounted first.
    if (window.turnstile) {
      render();
    } else {
      let script = document.getElementById(SCRIPT_ID);
      if (!script) {
        script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      script.addEventListener('load', render);
      script.addEventListener('error', degrade);
      // A blocked script often neither loads nor errors — it just never resolves. Without this
      // the user would sit on a disabled button forever, which is the dead end we must not build.
      timeoutId = setTimeout(() => {
        if (!window.turnstile) degrade();
      }, LOAD_TIMEOUT_MS);
    }

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (widgetIdRef.current && window.turnstile && window.turnstile.remove) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (_) { /* already gone */ }
      }
    };
  }, [required, sitekey]);

  const reset = () => {
    setToken('');
    if (widgetIdRef.current && window.turnstile && window.turnstile.reset) {
      try { window.turnstile.reset(widgetIdRef.current); } catch (_) { setUnavailable(true); }
    }
  };

  return {
    required,
    token,
    unavailable,
    // On Dev `required` is false, so `ready` is true immediately and the caller's submit button
    // behaves exactly as it did before Turnstile existed.
    ready: !required || !!token || unavailable,
    mountRef,
    reset,
  };
}

export default useTurnstile;
